import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, useUser, RedirectToSignIn } from "@clerk/clerk-react";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { useClerkAvailable } from "../clerk-context.js";

const API_URL = import.meta.env.VITE_API_URL ?? "";

interface InstalledSkill {
  slug: string;
  installedAt: string;
}

function PhoneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="2" width="14" height="20" rx="2" />
      <path d="M12 18h.01" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2 4 6v6c0 5.25 3.8 10.1 8 11.25C16.2 22.1 20 17.25 20 12V6l-8-4Z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8.5l3.5 3.5L13 5" />
    </svg>
  );
}

export function DashboardPage() {
  const clerkAvailable = useClerkAvailable();

  if (!clerkAvailable) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center px-6 py-16">
        <p className="text-sm text-kin-muted">Authentication is not configured.</p>
      </div>
    );
  }

  return <DashboardContent />;
}

function DashboardContent() {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const { user } = useUser();
  const [installedSkills, setInstalledSkills] = useState<InstalledSkill[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !API_URL) {
      setLoading(false);
      return;
    }

    async function fetchInstalled() {
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/api/marketplace/installed`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setInstalledSkills(data.skills ?? []);
        }
      } catch (_error) {
      } finally {
        setLoading(false);
      }
    }

    fetchInstalled();
  }, [isLoaded, isSignedIn, getToken]);

  if (!isLoaded) return null;
  if (!isSignedIn) return <RedirectToSignIn />;

  const firstName = user?.firstName ?? user?.emailAddresses[0]?.emailAddress?.split("@")[0];

  return (
    <div className="px-6 pb-16 pt-10 sm:px-8 sm:pb-20 sm:pt-14">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <section>
          <Badge variant="default">Dashboard</Badge>
          <h1 className="mt-4 font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
            Welcome back{firstName ? `, ${firstName}` : ""}.
          </h1>
          <p className="mt-3 text-base text-kin-muted">
            Manage your installed skills and get the app to run them.
          </p>
        </section>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {/* Installed skills */}
          <div className="lg:col-span-2">
            <div className="kin-surface rounded-3xl p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-kin-black">Installed Skills</h2>
                <Link to="/marketplace">
                  <Button variant="secondary" size="small">
                    Browse more
                  </Button>
                </Link>
              </div>

              {loading ? (
                <div className="mt-8 space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 animate-pulse rounded-2xl bg-kin-olive/10" />
                  ))}
                </div>
              ) : installedSkills.length === 0 ? (
                <div className="mt-8 rounded-2xl border border-dashed border-kin-olive/20 px-6 py-10 text-center">
                  <p className="text-sm text-kin-muted">No skills installed yet.</p>
                  <p className="mt-1 text-sm text-kin-muted">
                    Browse the{" "}
                    <Link
                      to="/marketplace"
                      className="font-medium text-kin-green-dark underline underline-offset-2"
                    >
                      marketplace
                    </Link>{" "}
                    to find skills that match your health goals.
                  </p>
                </div>
              ) : (
                <ul className="mt-4 space-y-3">
                  {installedSkills.map((skill) => (
                    <li key={skill.slug}>
                      <Link
                        to={`/marketplace/${skill.slug}`}
                        className="group flex items-center justify-between rounded-2xl bg-kin-cream/50 px-4 py-3 transition hover:bg-kin-cream"
                      >
                        <div className="flex items-center gap-3">
                          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-kin-green/15 text-kin-green-dark">
                            <CheckIcon />
                          </span>
                          <div>
                            <p className="text-sm font-semibold text-kin-black">{skill.slug}</p>
                            <p className="text-xs text-kin-muted">
                              Installed {new Date(skill.installedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs font-medium text-kin-green-dark opacity-0 transition group-hover:opacity-100">
                          View →
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="flex flex-col gap-4">
            {/* Download app CTA */}
            <div className="kin-surface-strong relative overflow-hidden rounded-3xl p-6">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, rgba(52,199,89,0.12) 0%, rgba(255,255,255,0.8) 100%)",
                }}
              />
              <div className="relative">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-kin-green/15 text-kin-green-dark">
                  <PhoneIcon />
                </span>
                <h3 className="mt-4 font-serif text-xl font-bold text-kin-black">
                  Run skills on your phone
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-kin-body/80">
                  Install the Kin app and your skills will be ready to run — with access to your
                  health data vault.
                </p>
                <span className="mt-4 inline-flex h-10 cursor-default items-center rounded-full bg-kin-black/40 px-5 text-sm font-semibold text-white">
                  Coming Soon
                </span>
              </div>
            </div>

            {/* Vault info */}
            <div className="kin-surface rounded-3xl p-6">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-kin-lavender/15 text-kin-lavender">
                <ShieldIcon />
              </span>
              <h3 className="mt-3 font-semibold text-kin-black">Vault is phone-only</h3>
              <p className="mt-2 text-sm leading-relaxed text-kin-body/80">
                Your health data vault lives exclusively on your device. Skills run in a secure
                sandbox with no internet access to your data. The web is for discovery only.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
